import { $ } from '../utils/dom';
import { updateConfirmSubmitEnabled } from './firma-legal-ui';

export interface SignaturePad {
  init(): void;
  prepare(): void;
  clear(): void;
  getDataUrl(): string | null;
  isEmpty(): boolean;
  resize(): void;
}

interface PointerLikeEvent {
  preventDefault(): void;
  pointerId?: number;
  clientX?: number;
  clientY?: number;
  touches?: TouchList;
  changedTouches?: TouchList;
}

export function createSignaturePad(): SignaturePad {
  let canvas: HTMLCanvasElement | null = null;
  let wrap: HTMLElement | null = null;
  let ctx: CanvasRenderingContext2D | null = null;
  let drawing = false;
  let hasInk = false;
  let lastX = 0;
  let lastY = 0;
  let resizeObserver: ResizeObserver | null = null;
  let cssWidth = 0;
  const cssHeight = 168;
  let signatureReady = false;

  function syncSubmitButton(): void {
    updateConfirmSubmitEnabled(!hasInk);
  }

  function setHasInk(value: boolean): void {
    hasInk = value;
    wrap?.classList.toggle('has-ink', hasInk);
    syncSubmitButton();
  }

  function eventPoint(e: PointerLikeEvent): { x: number; y: number } {
    const rect = canvas!.getBoundingClientRect();
    let clientX: number;
    let clientY: number;
    if (e.touches && e.touches.length) {
      clientX = e.touches[0]!.clientX;
      clientY = e.touches[0]!.clientY;
    } else if (e.changedTouches && e.changedTouches.length) {
      clientX = e.changedTouches[0]!.clientX;
      clientY = e.changedTouches[0]!.clientY;
    } else {
      clientX = e.clientX ?? 0;
      clientY = e.clientY ?? 0;
    }
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }

  function drawLine(x: number, y: number): void {
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastX = x;
    lastY = y;
    if (!hasInk) setHasInk(true);
  }

  function resize(): void {
    if (!canvas || !wrap) return;
    const rect = wrap.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width));
    const height = cssHeight;
    if (width < 2 || height < 2) return;

    const dpr = window.devicePixelRatio || 1;
    let snapshot: HTMLCanvasElement | null = null;
    if (hasInk && canvas.width > 0 && canvas.height > 0) {
      snapshot = document.createElement('canvas');
      snapshot.width = canvas.width;
      snapshot.height = canvas.height;
      snapshot.getContext('2d')?.drawImage(canvas, 0, 0);
    }

    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    cssWidth = width;

    ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.6;
    ctx.strokeStyle = '#153152';

    if (snapshot && snapshot.width > 0) {
      ctx.drawImage(snapshot, 0, 0, snapshot.width, snapshot.height, 0, 0, width, height);
    }
  }

  function startDraw(e: PointerLikeEvent): void {
    if (!ctx) return;
    e.preventDefault();
    drawing = true;
    if (canvas?.setPointerCapture && e.pointerId != null) {
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
    }
    const p = eventPoint(e);
    lastX = p.x;
    lastY = p.y;
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
  }

  function moveDraw(e: PointerLikeEvent): void {
    if (!drawing || !ctx) return;
    e.preventDefault();
    const p = eventPoint(e);
    drawLine(p.x, p.y);
  }

  function endDraw(e: PointerLikeEvent): void {
    if (!drawing) return;
    drawing = false;
    if (canvas?.releasePointerCapture && e.pointerId != null) {
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
    }
  }

  function bindEvents(): void {
    if (!canvas) return;
    if (window.PointerEvent) {
      canvas.addEventListener('pointerdown', startDraw as EventListener);
      canvas.addEventListener('pointermove', moveDraw as EventListener);
      canvas.addEventListener('pointerup', endDraw as EventListener);
      canvas.addEventListener('pointerleave', endDraw as EventListener);
      canvas.addEventListener('pointercancel', endDraw as EventListener);
    } else {
      canvas.addEventListener('touchstart', startDraw as EventListener, { passive: false });
      canvas.addEventListener('touchmove', moveDraw as EventListener, { passive: false });
      canvas.addEventListener('touchend', endDraw as EventListener, { passive: false });
      canvas.addEventListener('touchcancel', endDraw as EventListener, { passive: false });
      canvas.addEventListener('mousedown', startDraw as EventListener);
      canvas.addEventListener('mousemove', moveDraw as EventListener);
      canvas.addEventListener('mouseup', endDraw as EventListener);
      canvas.addEventListener('mouseleave', endDraw as EventListener);
    }
    $('btn-clear-firma')?.addEventListener('click', clear);
  }

  function init(): void {
    if (signatureReady) return;
    canvas = $('firma-canvas') as HTMLCanvasElement | null;
    wrap = $('signature-pad');
    if (!canvas || !wrap) return;
    bindEvents();
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        if (!$('view-confirm')?.classList.contains('hidden')) resize();
      });
      resizeObserver.observe(wrap);
    } else {
      window.addEventListener('resize', resize);
    }
    signatureReady = true;
  }

  function prepare(): void {
    init();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        resize();
        clear();
      });
    });
  }

  function clear(): void {
    if (!canvas || !ctx) return;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width || cssWidth || 300;
    ctx.clearRect(0, 0, w, cssHeight);
    setHasInk(false);
  }

  function getDataUrl(): string | null {
    if (!hasInk || !canvas) return null;
    return canvas.toDataURL('image/png');
  }

  function isEmpty(): boolean {
    return !hasInk;
  }

  return { init, prepare, clear, getDataUrl, isEmpty, resize };
}
